import React, { FC, useEffect, useState } from 'react';
// @ts-ignore
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '~Assets/Css/custom-calendar-styles.css';
import _ from 'lodash';
import { gql, useQuery } from '@apollo/client';
import { useI18n, useSelector, useStaffCan, useDates, useButton, useLicenses } from '~Hooks';
import {
  GetCategoriesQuery,
  GetVenueTableEventsSnapshots,
  GetVenueTableEventsSnapshotsVariables,
} from './__queries__';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import { DateTime } from 'luxon';
import { Box } from '~Styles';
import {
  ReportWrapper,
  CalendarWrapper,
  Prev,
  Next,
  Wrapper,
  LargeCalendarWrapper,
  MobileDateInput,
  PrintAllStaffReportsButton,
  ToggleReportButton,
} from './styles';
import Loader from '~ComponentsImproved/Loader';
import SelectOptionModal from '~Components/SelectOptionModal';
import { useReport } from './Hooks/useReport';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Heading from '~ComponentsImproved/Typography/Heading';
import { CountAndAmount } from '@baemingo/report-events/dist/types';
import ReportView from './View';
import { PARKED_ORDER_TABLE_ID } from '~Scenes/ParkedOrders/consts';
import DateInput from '~ComponentsImproved/DateInput';

const GET_CATEGORIES = gql`
  query GetCategoriesQuery {
    user {
      id
      company {
        id
        country
        defaultLanguage
        categories {
          id
          name
          parentId
        }
      }
    }
  }
`;

const GET_DELETED_CATEGORIES = gql`
  query GetDeletedCategoriesQuery {
    user {
      id
      company {
        id
        country
        defaultLanguage
        categories(filter: { deleted: true }) {
          id
          name
          parentId
        }
      }
    }
  }
`;

const TABLE_EVENTS_SNAPSHOTS_QUERY = gql`
  query GetVenueTableEventsSnapshots($venueId: ID!, $date: DateTime!) {
    Venue(id: $venueId) {
      id
      eventsSnapshot(date: $date)
    }
  }
`;

/** Cheque from tableSnapshot (returned as JSON so we made a custom type) */
export interface Cheque {
  id: string;
  amount: number;
  discount: number;
  paidAmount: number;
  coverage: number;
  createdAt: string;
  name: string | null;
  printed: boolean;
  stage: string;
  tableId: string;
}

/** Displays the selected day’s venue report (if the user has report permissions) and individual staff reports if staff reporting is enabled. */
const Reports: FC = () => {
  const { i18n } = useI18n();
  const dates = useDates();
  const licenses = useLicenses();

  const language = useSelector((state) => state.language);
  let lang: string = language;
  if (language == 'se') lang = 'sv';

  const [, , venueId] = location.hash.split('/');

  const thisStaff = useSelector((state) => state.staff);
  const staffCan = useStaffCan();
  const staffs = useSelector((state) => state.companyStaff?.staffs ?? []);

  const currentDate = new Date();

  const [showSelectStaffReport, setShowSelectStaffReport] = useState<boolean>(false);
  const [staffId, setStaffId] = useState<string | null>(null);
  const [chosenDate, setChosenDate] = useState<Date>(currentDate);

  const reportGenerator = useReport(venueId, chosenDate);

  const showFullReportButton = useButton({
    onClick: () => setStaffId(null),
  });

  const selectStaffReportButton = useButton({
    onClick: () => setShowSelectStaffReport(true),
    disabled: reportGenerator.staffWithData.length === 0,
    disabledToast: i18n('Report.NoStaffWithActivity'),
  });

  const printAllStaffReportsButton = useButton({
    onClick: () => reportGenerator.printMultipleReports(allCategories),
    disabled: reportGenerator.loading,
  });

  const tableSnapshotQuery = useQuery<
    GetVenueTableEventsSnapshots,
    GetVenueTableEventsSnapshotsVariables
  >(TABLE_EVENTS_SNAPSHOTS_QUERY, {
    variables: {
      venueId: venueId,
      date: dates.getAccountingEndDate(chosenDate), // What tables looked like at the end of the day
    },
    skip: !venueId || !licenses.hasTSR,
    fetchPolicy: 'network-only',
  });

  const cheques =
    (tableSnapshotQuery.data?.Venue?.eventsSnapshot?.cheques as Cheque[] | undefined) ?? [];

  /** If user has TSR --> Go through all cheques to sum them up with count & amount */
  const openCheques: CountAndAmount | null = licenses.hasTSR
    ? cheques
        // Filter out cheques created in the POS mode in handy
        .filter((c) => !c.tableId?.startsWith('POS_'))
        .filter((c) => c.tableId !== PARKED_ORDER_TABLE_ID) // Filter out cheques that is parked orders
        .reduce(
          (acc, c) => ({
            count: acc.count + 1,
            amount: acc.amount + (c.amount - c.discount - c.paidAmount),
          }),
          { count: 0, amount: 0 },
        )
    : null;

  const categoriesQuery = useQuery<GetCategoriesQuery>(GET_CATEGORIES);
  const deletedCategoriesQuery = useQuery<GetCategoriesQuery>(
    GET_DELETED_CATEGORIES,
  ); /* Include deleted categories to make sure no data is lost/hidden */

  const allCategories = [
    ...(categoriesQuery.data?.user?.company?.categories ?? []),
    ...(deletedCategoriesQuery.data?.user?.company?.categories ?? []),
  ];

  /* If this is a regular staff, force them to only see their own data */
  useEffect(() => {
    if (staffs.length === 0 || !thisStaff || staffCan.REPORTS) {
      return;
    }

    /* When this is set - we only show this specific staff's report */
    setStaffId(thisStaff.id);
  }, [thisStaff?.id]);

  const prevButton = useButton({
    onClick: () => {
      const prev = DateTime.fromJSDate(chosenDate).minus({ days: 1 });
      setChosenDate(prev.toJSDate());
    },
  });

  const nextButton = useButton({
    onClick: () => {
      const today = DateTime.now().startOf('day'); /* Get today's date without time */
      const chosen = DateTime.fromJSDate(chosenDate).startOf('day');

      if (chosen.hasSame(today, 'day')) return; /* Prevent moving past today */

      const next = chosen.plus({ days: 1 });
      setChosenDate(next.toJSDate());
    },
    disabled: DateTime.fromJSDate(chosenDate).hasSame(DateTime.fromJSDate(currentDate), 'day'),
  });

  const isHandyBreakpoint = window.innerWidth <= 600;
  const hasStaff = staffs.length > 0;

  if (categoriesQuery.loading || deletedCategoriesQuery.loading) {
    return (
      <Box
        style={{ height: '50vh' }}
        display="flex"
        alignItems="center"
        flexDirection="column"
        gap="small"
        justifyContent="center">
        <Loader color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'} />
        <Paragraph
          level={3}
          color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'}>
          {i18n('Report.Loading')}
        </Paragraph>
      </Box>
    );
  }

  return (
    <div>
      {!isHandyBreakpoint && <BackTopBar title={i18n('Menu.Reports')} />}

      <Wrapper>
        <CalendarWrapper>
          {hasStaff && staffCan.REPORTS && (
            <Box display="flex" gap="small" marginBottom="medium">
              <ToggleReportButton
                {...showFullReportButton}
                selected={!(staffId || showSelectStaffReport)}>
                {i18n('Report.ViewFullReport')}
              </ToggleReportButton>
              <ToggleReportButton
                {...selectStaffReportButton}
                selected={Boolean(staffId || showSelectStaffReport)}>
                {i18n('Report.ViewStaffReport')}
              </ToggleReportButton>
            </Box>
          )}

          <MobileDateInput>
            <DateInput
              selectedDate={chosenDate ? new Date(chosenDate) : null}
              setSelectedDate={(date: any) => {
                if (date instanceof Date) {
                  setChosenDate(date);
                }
              }}
              isDark
              maxDate={new Date()}
            />
          </MobileDateInput>

          <LargeCalendarWrapper>
            <Calendar
              onChange={(date: any) => setChosenDate(date)}
              value={chosenDate}
              locale={lang}
              maxDate={currentDate}
            />
            <Box display="flex" justifyContent="center" alignItems="center" marginTop="medium">
              <Prev {...prevButton} />
              <Heading level={3}>{DateTime.fromJSDate(chosenDate).toFormat('yyyy-MM-dd')}</Heading>
              <Next {...nextButton} />
            </Box>
          </LargeCalendarWrapper>

          {hasStaff && staffCan.REPORTS && (
            <Box display="flex" flexDirection="column" gap="small" marginTop="medium">
              <PrintAllStaffReportsButton {...printAllStaffReportsButton}>
                {i18n('Report.PrintAllStaffReports')}
              </PrintAllStaffReportsButton>
            </Box>
          )}
        </CalendarWrapper>

        <ReportWrapper>
          {tableSnapshotQuery.loading ? (
            <Box
              style={{ height: '50vh' }}
              display="flex"
              alignItems="center"
              flexDirection="column"
              gap="small"
              justifyContent="center">
              <Loader
                color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'}
              />
              <Paragraph
                level={3}
                color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'}>
                {i18n('Report.Loading')}
              </Paragraph>
            </Box>
          ) : (
            <ReportView
              key={staffId}
              selectedStaffId={staffId}
              chosenDate={chosenDate}
              allCategories={allCategories}
              openCheques={openCheques}
            />
          )}
        </ReportWrapper>
      </Wrapper>

      {/* Modals */}
      {thisStaff === null && <LoginModal />}
      {showSelectStaffReport && (
        <SelectOptionModal
          title={i18n('Report.StaffWithActivity')}
          width="90vw"
          size={staffs.length > 16 ? 'sm' : 'md'}
          responsiveBoxes
          options={reportGenerator.staffWithData
            .map((s) => s.name)
            .sort((a, b) => a.localeCompare(b))}
          onSelect={(name) => {
            setStaffId(String(staffs.find((s) => s.name === name)?.id));
            setShowSelectStaffReport(false);
          }}
          onCancel={() => setShowSelectStaffReport(false)}
        />
      )}
    </div>
  );
};

export default Reports;
